import React, { useState, useMemo, useRef } from "react";
import Page from "../components/Page";
import {
  IconFileSpreadsheet,
  IconPrinter,
  IconSearch,
  IconReceiptTax,
  IconReceipt,
  IconCalendar,
  IconCloudCheck,
  IconCloudOff
} from "@tabler/icons-react";
import { useBikriKhata } from "../controllers/accounting.controller";
import {
  NEPALI_MONTHS,
  getCurrentBsDate,
  getAvailableFiscalYears
} from "../helpers/NepaliDateHelper";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

export default function BikriKhataPage() {
  const currentBs = useMemo(() => getCurrentBsDate(), []);
  const fiscalYears = useMemo(() => getAvailableFiscalYears(), []);

  const [selectedFy, setSelectedFy] = useState(currentBs.fiscalYear || "2083/84");
  const [selectedMonth, setSelectedMonth] = useState(currentBs.bsMonth || 1);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("schedule8"); // 'schedule8' | 'customerSummary'

  const { data, entries, summary, customerSummary, isLoading } = useBikriKhata({
    fiscalYear: selectedFy,
    nepaliMonth: selectedMonth || null
  });

  // Filter entries by search
  const filteredEntries = useMemo(() => {
    if (!entries || !Array.isArray(entries)) return [];
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.invoice_no?.toLowerCase().includes(q) ||
        String(e.invoice_id).includes(q) ||
        e.customer_name?.toLowerCase().includes(q) ||
        e.customer_pan?.toLowerCase().includes(q) ||
        e.nepali_date?.includes(q)
    );
  }, [entries, search]);

  // Export to Excel (Schedule 8 format)
  const handleExportExcel = () => {
    try {
      const monthObj = NEPALI_MONTHS.find((m) => m.id === Number(selectedMonth));
      const monthTitle = monthObj ? monthObj.name : "All Months";

      const headers = [
        "S.N.",
        "Miti (B.S.)",
        "Date (A.D.)",
        "Invoice / Bill No",
        "Buyer / Customer Name",
        "Buyer PAN",
        "Taxable Sales (NPR)",
        "13% Output VAT (NPR)",
        "Service Charge (NPR)",
        "Exempt Sales (NPR)",
        "Total Net Sales (NPR)",
        "Paid Amount (NPR)",
        "Due Balance (NPR)",
        "IRD Synced"
      ];

      const rows = filteredEntries.map((e, idx) => [
        idx + 1,
        e.nepali_date || "",
        e.invoice_date ? new Date(e.invoice_date).toISOString().substring(0, 10) : "",
        e.invoice_no || `INV-${e.invoice_id}`,
        e.customer_name || "Walk-in Customer",
        e.customer_pan || "N/A",
        Number(e.taxable_amount || 0).toFixed(2),
        Number(e.vat_amount || 0).toFixed(2),
        Number(e.service_charge || 0).toFixed(2),
        Number(e.exempt_amount || 0).toFixed(2),
        Number(e.total_amount || 0).toFixed(2),
        Number(e.paid_amount || 0).toFixed(2),
        Number(e.due_amount || 0).toFixed(2),
        e.is_ird_synced ? "Yes" : "No"
      ]);

      // Add Total Row
      rows.push([
        "TOTAL",
        "",
        "",
        "",
        "",
        "",
        Number(summary.total_taxable || 0).toFixed(2),
        Number(summary.total_vat || 0).toFixed(2),
        Number(summary.total_service_charge || 0).toFixed(2),
        Number(summary.total_exempt || 0).toFixed(2),
        Number(summary.total_sales || 0).toFixed(2),
        Number(summary.total_paid || 0).toFixed(2),
        Number(summary.total_due || 0).toFixed(2),
        ""
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([
        [`BIKRI KHATA (SALES BOOK) - SCHEDULE 8 (अनुसूची ८)`],
        [`Fiscal Year: ${selectedFy} | Month: ${monthTitle}`],
        [],
        headers,
        ...rows
      ]);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Bikri Khata");
      XLSX.writeFile(workbook, `Bikri_Khata_${selectedFy.replace("/", "-")}_${monthTitle}.xlsx`);
      toast.success("Bikri Khata exported to Excel successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel file");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Page className="print:p-0 print:bg-white">
      {/* ─── SCREEN-ONLY HEADER ─── */}
      <div className="space-y-6 print:hidden">
        {/* Top Header Card */}
        <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-violet-800 rounded-3xl p-6 md:p-8 text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md text-xs font-black uppercase tracking-wider mb-2">
              <IconReceiptTax size={14} /> IRD Schedule 8 (अनुसूची ८)
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">Bikri Khata (Sales Book)</h1>
            <p className="text-blue-100 text-sm mt-1 max-w-xl">
              Official sales register for Nepal VAT compliance. Records taxable sales, 13% Output VAT, service charges, customer PAN, and IRD sync status in Bikram Sambat.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <button
              onClick={handleExportExcel}
              className="py-3 px-5 bg-white text-blue-900 hover:bg-blue-50 rounded-2xl font-black text-xs flex items-center gap-2 transition shadow-lg shadow-black/10 active:scale-95"
            >
              <IconFileSpreadsheet size={18} className="text-blue-600" /> Export Excel (.xlsx)
            </button>
            <button
              onClick={handlePrint}
              className="py-3 px-5 bg-blue-600/60 hover:bg-blue-600 text-white rounded-2xl font-black text-xs flex items-center gap-2 transition border border-white/20 active:scale-95"
            >
              <IconPrinter size={18} /> Print Register
            </button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-3xl p-5 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Fiscal Year Selector */}
            <div className="flex items-center gap-3">
              <span className="text-xs font-black uppercase tracking-wider text-gray-400 flex items-center gap-1.5 shrink-0">
                <IconCalendar size={16} className="text-blue-500" /> Fiscal Year:
              </span>
              <select
                value={selectedFy}
                onChange={(e) => setSelectedFy(e.target.value)}
                className="select select-sm select-bordered rounded-xl bg-gray-50 dark:bg-gray-800 font-black text-xs outline-none"
              >
                {fiscalYears.map((fy) => (
                  <option key={fy} value={fy}>
                    FY {fy} (B.S.)
                  </option>
                ))}
              </select>
            </div>

            {/* Search Box */}
            <div className="relative flex-1 max-w-md">
              <IconSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search Invoice #, Customer, PAN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl py-2 pl-10 pr-4 text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>
          </div>

          {/* Nepali Month Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
            <button
              onClick={() => setSelectedMonth(null)}
              className={`px-3 py-1.5 rounded-xl text-xs font-black shrink-0 transition ${
                selectedMonth === null
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              Full Year
            </button>
            {NEPALI_MONTHS.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedMonth(m.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold shrink-0 transition ${
                  selectedMonth === m.id
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/20 font-black"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                {m.name} <span className="text-[10px] opacity-70">({m.nepaliName})</span>
              </button>
            ))}
          </div>
        </div>

        {/* KPI Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 block">Total Net Sales</span>
            <span className="text-xl font-black text-gray-900 dark:text-white mt-1 block">
              Rs. {Number(summary.total_sales || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">{summary.record_count || 0} Invoices</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-blue-600 block">Taxable Sales</span>
            <span className="text-xl font-black text-blue-600 mt-1 block">
              Rs. {Number(summary.total_taxable || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">13% VAT Base</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 block">13% Output VAT</span>
            <span className="text-xl font-black text-indigo-600 mt-1 block">
              Rs. {Number(summary.total_vat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">Collected Tax</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 block">Service Charge</span>
            <span className="text-xl font-black text-amber-600 mt-1 block">
              Rs. {Number(summary.total_service_charge || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">10% Charge</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 block">Total Collected</span>
            <span className="text-xl font-black text-emerald-600 mt-1 block">
              Rs. {Number(summary.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">Settled Payments</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-rose-600 block">Accounts Receivable</span>
            <span className="text-xl font-black text-rose-600 mt-1 block">
              Rs. {Number(summary.total_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-rose-400 mt-1 block">Pending Debtors</span>
          </div>
        </div>

        {/* View Switcher */}
        <div className="flex border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => setActiveTab("schedule8")}
            className={`py-3 px-5 text-xs font-black transition border-b-2 ${
              activeTab === "schedule8"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            Schedule 8 Sales Register ({filteredEntries.length})
          </button>
          <button
            onClick={() => setActiveTab("customerSummary")}
            className={`py-3 px-5 text-xs font-black transition border-b-2 ${
              activeTab === "customerSummary"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            Customer Wise Summary ({customerSummary?.length || 0})
          </button>
        </div>
      </div>

      {/* ─── PRINT-ONLY STATUTORY HEADER ─── */}
      <div className="hidden print:block mb-6 text-center space-y-1">
        <h2 className="text-xl font-bold uppercase">Bikri Khata (Sales Book)</h2>
        <h3 className="text-sm font-semibold">अनुसूची ८ (नियम २३ को उपनियम (१) को खण्ड (च) सँग सम्बन्धित)</h3>
        <p className="text-xs">
          करदाताको नाम: Hotel / Restaurant | आ.व.: {selectedFy} | महिना:{" "}
          {NEPALI_MONTHS.find((m) => m.id === Number(selectedMonth))?.name || "Full Year"}
        </p>
      </div>

      {/* ─── TAB 1: SCHEDULE 8 STATUTORY TABLE ─── */}
      {activeTab === "schedule8" && (
        <div className="bg-white dark:bg-restro-gray rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="table w-full text-left text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-850/80 border-b border-gray-150 dark:border-gray-800 text-[11px] font-black uppercase text-gray-500 tracking-wider">
                  <th className="py-4 px-3 text-center">क्र.सं.<br/>S.N.</th>
                  <th>मिति (B.S.)<br/>Date (A.D.)</th>
                  <th>बीजक नं.<br/>Invoice No.</th>
                  <th>खरिदकर्ताको नाम<br/>Buyer Name</th>
                  <th>पान नं.<br/>PAN No.</th>
                  <th className="text-right">करयोग्य बिक्री<br/>Taxable (Rs.)</th>
                  <th className="text-right">१३% भ्याट<br/>VAT 13% (Rs.)</th>
                  <th className="text-right">सेवा शुल्क<br/>Service Chg (Rs.)</th>
                  <th className="text-right">कर छुट<br/>Exempt (Rs.)</th>
                  <th className="text-right">जम्मा बिक्री<br/>Total Sales (Rs.)</th>
                  <th className="text-right">भुक्तान<br/>Paid (Rs.)</th>
                  <th className="text-right">बाँकी<br/>Due (Rs.)</th>
                  <th className="text-center print:hidden">IRD Sync</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-medium">
                {isLoading && (
                  <tr>
                    <td colSpan="13" className="py-12 text-center text-gray-400">
                      <span className="loading loading-spinner loading-md"></span>
                      <p className="mt-2 text-xs">Loading Bikri Khata entries...</p>
                    </td>
                  </tr>
                )}

                {!isLoading && filteredEntries.length === 0 && (
                  <tr>
                    <td colSpan="13" className="py-12 text-center text-gray-400">
                      No sales records found for the selected period.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  filteredEntries.map((e, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition">
                      <td className="py-3 px-3 text-center font-bold text-gray-400">{idx + 1}</td>
                      <td>
                        <div className="font-bold text-gray-900 dark:text-gray-100">{e.nepali_date || "—"}</div>
                        <div className="text-[10px] text-gray-400">
                          {e.invoice_date ? new Date(e.invoice_date).toISOString().substring(0, 10) : ""}
                        </div>
                      </td>
                      <td className="font-mono font-bold text-blue-700 dark:text-blue-400">
                        {e.invoice_no || `INV-${e.invoice_id}`}
                      </td>
                      <td>
                        <div className="font-bold text-gray-900 dark:text-white">
                          {e.customer_name || "Walk-in Customer"}
                        </div>
                        {e.customer_id && <div className="text-[10px] text-gray-400">{e.customer_id}</div>}
                      </td>
                      <td className="font-mono text-gray-600 dark:text-gray-300 font-bold">
                        {e.customer_pan || <span className="text-gray-300">N/A</span>}
                      </td>
                      <td className="text-right font-bold text-gray-900 dark:text-white">
                        {Number(e.taxable_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-bold text-indigo-600 dark:text-indigo-400">
                        {Number(e.vat_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-medium text-amber-600 dark:text-amber-400">
                        {Number(e.service_charge || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-medium text-gray-500 dark:text-gray-400">
                        {Number(e.exempt_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-black text-gray-900 dark:text-white">
                        {Number(e.total_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-medium text-emerald-600 dark:text-emerald-400">
                        {Number(e.paid_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-bold text-rose-600 dark:text-rose-400">
                        {Number(e.due_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-center print:hidden">
                        {e.is_ird_synced ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-black text-emerald-700 bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-300 px-2 py-0.5 rounded-full">
                            <IconCloudCheck size={12} /> Synced
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                            <IconCloudOff size={12} /> Local
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
              {filteredEntries.length > 0 && (
                <tfoot>
                  <tr className="bg-blue-50/70 dark:bg-blue-950/40 border-t-2 border-blue-500 font-black text-xs text-gray-900 dark:text-white">
                    <td colSpan="5" className="py-4 px-4 uppercase tracking-wider text-blue-800 dark:text-blue-300">
                      Total (जम्मा)
                    </td>
                    <td className="text-right font-black text-blue-800 dark:text-blue-300">
                      {Number(summary.total_taxable || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-indigo-700 dark:text-indigo-300">
                      {Number(summary.total_vat || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-amber-700 dark:text-amber-300">
                      {Number(summary.total_service_charge || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-gray-600 dark:text-gray-300">
                      {Number(summary.total_exempt || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-blue-900 dark:text-blue-200 text-sm">
                      {Number(summary.total_sales || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-emerald-700 dark:text-emerald-300">
                      {Number(summary.total_paid || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-rose-700 dark:text-rose-300">
                      {Number(summary.total_due || 0).toFixed(2)}
                    </td>
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB 2: CUSTOMER SUMMARY TABLE ─── */}
      {activeTab === "customerSummary" && (
        <div className="bg-white dark:bg-restro-gray rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="table w-full text-left text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-850/80 border-b border-gray-150 dark:border-gray-800 text-[11px] font-black uppercase text-gray-500 tracking-wider">
                  <th className="py-4 px-3 text-center">S.N.</th>
                  <th>Customer Name</th>
                  <th>PAN Number</th>
                  <th className="text-center">Invoices Count</th>
                  <th className="text-right">Taxable Sales (Rs.)</th>
                  <th className="text-right">Output VAT (Rs.)</th>
                  <th className="text-right">Total Sales (Rs.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-medium">
                {(!customerSummary || customerSummary.length === 0) && (
                  <tr>
                    <td colSpan="7" className="py-12 text-center text-gray-400">
                      No customer summary available.
                    </td>
                  </tr>
                )}
                {customerSummary?.map((c, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition">
                    <td className="py-3 px-3 text-center font-bold text-gray-400">{idx + 1}</td>
                    <td className="font-bold text-gray-900 dark:text-white">
                      {c.customer_name || "Walk-in Customer"}
                    </td>
                    <td className="font-mono font-bold text-gray-600 dark:text-gray-300">
                      {c.customer_pan || "N/A"}
                    </td>
                    <td className="text-center font-bold text-gray-600 dark:text-gray-300">
                      {c.invoice_count}
                    </td>
                    <td className="text-right font-bold text-gray-900 dark:text-white">
                      {Number(c.taxable_amount || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-bold text-indigo-600 dark:text-indigo-400">
                      {Number(c.vat_amount || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-gray-900 dark:text-white">
                      {Number(c.total_amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Page>
  );
}
